/**
 * Ficha de una sola palabra aprendida.
 * Guardado vía {@link SavedVocabularyContext.saveLearnedVocab} / quitar por palabra.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { usersAPI, type LearnedWord } from '@/services/api/users.api';
import { Spinner } from '@/components/atoms/Spinner';
import { VocabularyFlashcard } from '@/components/molecules/VocabularyFlashcard';
import { buildRoute, ROUTES } from '@/constants/routes';
import { learnedWordToVocabItem } from '@/pages/student/ProgressPage/utils/learnedWordToVocabItem';
import { useVocabularyStudyTimer } from '@/hooks/useVocabularyStudyTimer';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import type { VocabItem } from '@/types/vocabulary.types';
import styles from './LearnedWordVocabLayout.module.scss';

function isPeriod(v: string | undefined): v is 'week' | 'month' {
  return v === 'week' || v === 'month';
}

export function LearnedWordDetailPage() {
  const { t } = useTranslation();
  const { period, wordId } = useParams<{ period: string; wordId: string }>();
  const navigate = useNavigate();
  const [learnedWord, setLearnedWord] = useState<LearnedWord | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const { saveLearnedVocab, removeItem, isSaved } = useSavedVocabulary();

  const listPath =
    isPeriod(period) && period
      ? buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period })
      : ROUTES.STUDENT.PROGRESS;

  useEffect(() => {
    if (!wordId || !isPeriod(period)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const row = await usersAPI.getLearnedWordById(wordId);
        if (!cancelled) setLearnedWord(row);
      } catch {
        if (!cancelled) {
          setLearnedWord(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wordId, period]);

  const vocabItem: VocabItem | null = useMemo(
    () => (learnedWord ? learnedWordToVocabItem(learnedWord) : null),
    [learnedWord]
  );

  const isWordReady = Boolean(vocabItem) && !loading && !loadError;

  useVocabularyStudyTimer(isWordReady);

  const handleToggleSaveVocab = useCallback(
    async (v: VocabItem) => {
      if (isSaved(v.word)) {
        await removeItem(v.word);
        return;
      }
      await saveLearnedVocab(v);
    },
    [isSaved, removeItem, saveLearnedVocab]
  );

  if (!isPeriod(period) || !wordId) {
    return <Navigate to={ROUTES.STUDENT.PROGRESS} replace />;
  }

  if (loading) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (loadError || !vocabItem) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <p>{t('learnedWordsList.wordNotFound')}</p>
          <button
            type="button"
            data-no-press-scale=""
            className={styles.vocabPage__retry}
            onClick={() => navigate(listPath)}
          >
            {t('learnedWordsList.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.vocabPage}>
      <div className={styles.vocabPage__card}>
        <VocabularyFlashcard
          className={styles.vocabPage__vocabStretch}
          item={vocabItem}
          isSaved={isSaved(vocabItem.word)}
          onToggleSave={handleToggleSaveVocab}
        />
      </div>
    </div>
  );
}

LearnedWordDetailPage.displayName = 'LearnedWordDetailPage';
